<script setup>
import {
  NCard, NSpace, NH1, NH2, NH3, NP, NDivider,
  NButton, NIcon, NGrid, NGridItem, NStatistic, NTag, NGradientText
} from 'naive-ui';
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { 
  Languages, Search, Clock, MapPin, Stethoscope, 
  Bookmark, MessageSquare, UserCheck, Vote, Users, 
  Globe, Calendar, Video, FileText 
} from 'lucide-vue-next'
import { dt } from '@/utils/i18n'

const router = useRouter()
const { t, locale } = useI18n()

// Stats with Lucide icons
const featureStats = [
  {
    count: 10,
    label: dt('features.stats.languages'),
    icon: Languages,
    color: '#36d1dc'
  },
  {
    count: 100,
    label: dt('features.stats.searchOptions'),
    icon: Search,
    color: '#5b86e5'
  },
  {
    count: 5,
    label: dt('features.stats.minutesToFind'),
    icon: Clock,
    color: '#ef473a'
  }
]

// Main features icons
const mainFeatureIcons = [Languages, MapPin, Stethoscope]

// Additional features icons
const additionalFeatureIcons = [Bookmark, MessageSquare, UserCheck]

// Community features icons
const communityFeatureIcons = [Vote, Users, Globe]

// Coming soon feature icons
const comingSoonIcons = [Calendar, Video, FileText]

const goToSearch = () => {
  router.push('/search')
}
</script>

<template>
  <div class="features-container">
    <!-- Hero Section -->
    <div class="hero-section">
      <n-h1 class="hero-title">
        <n-gradient-text :gradient="{ from: '#5b86e5', to: '#36d1dc', deg: 45 }">
          {{ dt('features.heroTitle') }}
        </n-gradient-text>
      </n-h1>
      <n-p class="hero-subtitle">{{ dt('features.heroSubtitle') }}</n-p>
    </div>

    <!-- Core Features Overview -->
    <n-card class="overview-card">
      <div class="overview-content">
        <div class="overview-text">
          <n-h2 class="overview-heading">{{ dt('features.mainFeaturesTitle') }}</n-h2>
          <n-p class="overview-paragraph">{{ dt('features.mainFeaturesOverview') }}</n-p>
        </div>
        <div class="overview-visual">
          <div class="feature-highlight"></div>
        </div>
      </div>
    </n-card>

    <!-- Stats Section -->
    <div class="stats-section">
      <n-grid :cols="3" :x-gap="16" :collapsed-rows="3" responsive="screen" :collapsed="['xs', 's']">
        <n-grid-item v-for="(stat, index) in featureStats" :key="index">
          <n-statistic :label="t(stat.label)" :value="stat.count">
            <template #prefix>
              <component :is="stat.icon" :size="24" :color="stat.color" />
            </template>
          </n-statistic>
        </n-grid-item>
      </n-grid>
    </div>

    <!-- Main Features Section -->
    <div class="features-section">
      <n-h2 class="section-title">{{ dt('features.mainFeaturesTitle') }}</n-h2>
      <div class="features-grid">
        <div class="feature-item" v-for="(icon, index) in mainFeatureIcons" :key="`main-${index}`">
          <div :class="`feature-icon ${['language-search', 'location-search', 'specialization-search'][index]}`">
            <component :is="icon" size="32" color="#ffffff" />
          </div>
          <n-h3 class="feature-heading">{{ dt(`features.mainFeatures.${['languageSearchTitle', 'locationSearchTitle', 'specializationSearchTitle'][index]}`) }}</n-h3>
          <n-p class="feature-text">{{ dt(`features.mainFeatures.${['languageSearchText', 'locationSearchText', 'specializationSearchText'][index]}`) }}</n-p>
        </div>
      </div>
    </div>

    <!-- Additional Features Section -->
    <div class="additional-features">
      <n-h2 class="section-title">{{ dt('features.additionalFeaturesTitle') }}</n-h2>
      <div class="features-grid">
        <div class="feature-item" v-for="(icon, index) in additionalFeatureIcons" :key="`additional-${index}`">
          <div :class="`feature-icon ${['bookmarking', 'reviews', 'provider-claim'][index]}`">
            <component :is="icon" size="32" color="#ffffff" />
          </div>
          <n-h3 class="feature-heading">{{ dt(`features.additionalFeatures.${['bookmarkingTitle', 'reviewsTitle', 'providerClaimTitle'][index]}`) }}</n-h3>
          <n-p class="feature-text">{{ dt(`features.additionalFeatures.${['bookmarkingText', 'reviewsText', 'providerClaimText'][index]}`) }}</n-p>
        </div>
      </div>
    </div>

    <!-- Community Features Section -->
    <div class="community-features">
      <n-h2 class="section-title">{{ dt('features.communityTitle') }}</n-h2>
      <div class="community-grid">
        <div class="community-item" v-for="(icon, index) in communityFeatureIcons" :key="`community-${index}`">
          <div :class="`community-icon ${['voting', 'contributions', 'translations'][index]}`">
            <component :is="icon" size="32" color="#ffffff" />
          </div>
          <n-h3 class="feature-heading">{{ dt(`features.communityFeatures.${['votingTitle', 'contributionsTitle', 'translationsTitle'][index]}`) }}</n-h3>
          <n-p class="feature-text">{{ dt(`features.communityFeatures.${['votingText', 'contributionsText', 'translationsText'][index]}`) }}</n-p>
          <div class="language-tags" v-if="index === 2">
            <n-tag type="success" size="small">हिन्दी</n-tag>
            <n-tag type="info" size="small">বাংলা</n-tag>
            <n-tag type="warning" size="small">తెలుగు</n-tag>
            <n-tag type="error" size="small">தமிழ்</n-tag>
            <n-tag type="success" size="small">മലയാളം</n-tag>
          </div>
        </div>
      </div>
    </div>

    <!-- Coming Soon Section -->
    <div class="coming-soon-section">
      <n-h2 class="section-title">{{ dt('features.comingSoonTitle') }}</n-h2>
      <div class="coming-soon-grid">
        <div class="coming-soon-item" v-for="(icon, index) in comingSoonIcons" :key="`coming-${index}`">
          <div :class="`coming-soon-icon ${['appointments', 'telemed', 'health-records'][index]}`">
            <component :is="icon" size="32" color="#ffffff" />
          </div>
          <n-h3 class="feature-heading">{{ dt(`features.comingSoon.${['appointmentsTitle', 'telemedTitle', 'healthRecordsTitle'][index]}`) }}</n-h3>
          <n-p class="feature-text">{{ dt(`features.comingSoon.${['appointmentsText', 'telemedText', 'healthRecordsText'][index]}`) }}</n-p>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="join-section">
      <n-card class="join-card">
        <n-h2 class="join-heading">{{ dt('features.ctaTitle') }}</n-h2>
        <n-p class="join-text">{{ dt('features.ctaText') }}</n-p>
        <n-button type="primary" size="large" class="join-button" @click="goToSearch">
          {{ dt('features.ctaButton') }}
        </n-button>
      </n-card>
    </div>
  </div>
</template>

<style scoped>
/* Global text styles to ensure proper wrapping */
:deep(.n-h1), 
:deep(.n-h2), 
:deep(.n-h3), 
:deep(.n-p),
:deep(.n-gradient-text) {
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
  word-break: break-word !important;
  white-space: normal !important;
  max-width: 100% !important;
  hyphens: auto !important;
}

:deep(.n-statistic-label) {
  white-space: normal !important;
  word-break: break-word !important;
}

.hero-title,
.overview-heading,
.section-title,
.feature-heading,
.join-heading {
  width: 100%;
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
}

.features-container {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 16px;
  overflow-x: hidden;
  box-sizing: border-box;
}

.hero-section {
  text-align: center;
  margin-bottom: 10px;
  padding: 40px 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.hero-subtitle {
  font-size: 1.2rem;
  max-width: 100%;
  margin: 16px auto;
  color: #555;
  word-break: break-word;
}

.overview-card {
  margin-bottom: 40px;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
}

.overview-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 30px;
  width: 100%;
}

.overview-text {
  flex: 1;
  padding: 0 10px;
  max-width: 100%;
  word-break: break-word;
}

.overview-visual {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.overview-paragraph {
  margin: 16px 0;
  font-size: 1rem;
  line-height: 1.6;
  max-width: 100%;
  word-break: break-word;
}

.feature-highlight {
  width: 240px;
  height: 240px;
  background: linear-gradient(45deg, #36d1dc, #5b86e5);
  border-radius: 50%;
  position: relative;
  animation: pulse 3s infinite;
}

.stats-section {
  margin: 40px 0;
  width: 100%;
  box-sizing: border-box;
}

.section-title {
  text-align: center;
  margin-bottom: 30px;
  font-size: 1.5rem;
  position: relative;
  width: 100%;
  max-width: 100%;
}

.section-title::after {
  content: "";
  display: block;
  width: 50px;
  height: 3px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  margin: 10px auto 0;
  border-radius: 2px;
}

.features-grid,
.community-grid,
.coming-soon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
  width: 100%;
  box-sizing: border-box;
}

.feature-item,
.community-item,
.coming-soon-item {
  padding: 20px;
  border-radius: 10px;
  background-color: #f9f9f9;
  transition: transform 0.3s, box-shadow 0.3s;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.feature-text {
  width: 100%;
  word-break: break-word;
  overflow-wrap: break-word;
}

.feature-item:hover,
.community-item:hover,
.coming-soon-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.feature-icon,
.community-icon,
.coming-soon-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.language-search {
  background: linear-gradient(45deg, #36d1dc, #5b86e5);
}

.location-search {
  background: linear-gradient(45deg, #FF5F6D, #FFC371);
}

.specialization-search {
  background: linear-gradient(45deg, #11998e, #38ef7d);
}

.bookmarking {
  background: linear-gradient(45deg, #8E2DE2, #4A00E0);
}

.reviews {
  background: linear-gradient(45deg, #F2994A, #F2C94C);
}

.provider-claim {
  background: linear-gradient(45deg, #1FA2FF, #12D8FA, #A6FFCB);
}

.voting {
  background: linear-gradient(45deg, #ED4264, #FFEDBC);
}

.contributions {
  background: linear-gradient(45deg, #7F00FF, #E100FF);
}

.translations {
  background: linear-gradient(45deg, #00C9FF, #92FE9D);
}

.appointments {
  background: linear-gradient(45deg, #FC466B, #3F5EFB);
}

.telemed {
  background: linear-gradient(45deg, #0BAB64, #3BB78F);
}

.health-records {
  background: linear-gradient(45deg, #FF512F, #DD2476);
}

.language-tags {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.join-section {
  margin: 40px 0;
  width: 100%;
  box-sizing: border-box;
}

.join-card {
  text-align: center;
  padding: 30px 20px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(54, 209, 220, 0.1), rgba(91, 134, 229, 0.1));
  width: 100%;
  box-sizing: border-box;
}

.join-text {
  max-width: 800px;
  margin: 16px auto;
  font-size: 1rem;
  word-break: break-word;
}

.join-button {
  margin-top: 16px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  border: none;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(91, 134, 229, 0.4);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(91, 134, 229, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(91, 134, 229, 0);
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: 30px 12px;
  }

  .hero-subtitle {
    font-size: 1rem;
  }
  
  .overview-content {
    flex-direction: column;
    gap: 20px;
  }

  .feature-highlight {
    width: 180px;
    height: 180px;
  }
  
  .overview-paragraph {
    font-size: 0.95rem;
  }
  
  .section-title {
    font-size: 1.3rem;
    margin-bottom: 20px;
  }
  
  .feature-icon,
  .community-icon,
  .coming-soon-icon {
    width: 50px;
    height: 50px;
  }
  
  .feature-item,
  .community-item,
  .coming-soon-item {
    padding: 16px;
  }
  
  .join-card {
    padding: 20px 16px;
  }
}

@media (max-width: 480px) {
  .features-container {
    padding: 12px;
  }
  
  .feature-highlight {
    width: 150px;
    height: 150px;
  }
  
  .features-grid,
  .community-grid,
  .coming-soon-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-section .n-statistic {
    font-size: 0.9rem;
  }
  
  .section-title {
    font-size: 1.2rem;
  }
  
  .feature-heading {
    font-size: 1.1rem;
  }
}
</style>